import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { FiArrowRight } from 'react-icons/fi';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';

export interface BlogAuthor {
  name: string;
  role?: string;
  avatar?: string;
  linkedIn?: string;
}

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  image?: string;
  author?: BlogAuthor;
  publishedAt?: string;
}

export const blogPosts: BlogPost[] = [
  {
    slug: "logistics-partner-comparison",
    title: "Aggregators like Shiprocket vs Direct Logistics Partner?",
    excerpt: "When to stay on an aggregator, when to go direct, and how to build the right logistics stack for your D2C brand.",
    category: "Operations",
    readTime: "5 min read",
    image: "/blog/logistics_partner.png",
    author: {
      name: "Yash Goyal",
      avatar: "/team/yash.jpeg",
      linkedIn: "https://www.linkedin.com/in/yash-goyal/",
    },
    publishedAt: "2026-03-08",
  },
  {
    slug: "checkout-platform-comparison",
    title: "Should You Use GoKwik/Shopflo or Stick with Native Shopify Checkout?",
    excerpt: "Our honest take after speaking to dozens of founders about third-party checkouts vs native Shopify for Indian D2C brands.",
    category: "Conversion",
    readTime: "3 min read",
    image: "/blog/checkout_platform.png",
    author: {
      name: "Yash Goyal",
      avatar: "/team/yash.jpeg",
      linkedIn: "https://www.linkedin.com/in/yash-goyal/",
    },
    publishedAt: "2026-03-07",
  },
  {
    slug: "improve-repeat-ratio",
    title: "How to Improve Your Repeat Ratio: A Guide for Indian D2C Brands",
    excerpt: "Building a sustainable D2C business isn't about acquiring more customers. It's about getting existing customers to buy again.",
    category: "Retention",
    readTime: "8 min read",
    image: "/blog/repeat-ratio-hero.png",
    author: {
      name: "Yash Goyal",
      avatar: "/team/yash.jpeg",
      linkedIn: "https://www.linkedin.com/in/yash-goyal/",
    },
    publishedAt: "2026-03-05",
  },
  {
    slug: "rto-killing-margins",
    title: "Why Your RTO Rate is Killing Your Margins (And What Your Data is Telling You)",
    excerpt: "Every returned order costs you 2-3x the shipping. Here's how to spot high-risk orders before they ship.",
    category: "Operations",
    readTime: "7 min read",
    image: "/blog/rto_image.png",
    author: {
      name: "Yash Goyal",
      avatar: "/team/yash.jpeg",
      linkedIn: "https://www.linkedin.com/in/yash-goyal/",
    },
  },
];

export default function BlogCarousel() {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  const scroll = (direction: 'left' | 'right') => {
    const el = carouselRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.8;
    el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
  };

  return (
    <section className="py-20 px-8 lg:px-16 bg-base-200">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="mb-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Resources & Insights
          </h2>
        </div>
        <p className="text-lg opacity-70 mb-8">
          Learn how leading e-commerce brands use data to drive growth, optimize conversions, and build sustainable businesses.
        </p>

        {/* Blog Carousel */}
        <div className="relative">
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 btn btn-circle btn-sm btn-primary shadow-md"
            >
              <LuChevronLeft className="w-4 h-4" />
            </button>
          )}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 btn btn-circle btn-sm btn-primary shadow-md"
            >
              <LuChevronRight className="w-4 h-4" />
            </button>
          )}
        <div ref={carouselRef} className="carousel rounded-box gap-8 w-full">
          {blogPosts.map((post) => (
            <div key={post.slug} className="carousel-item basis-[85%] sm:basis-[45%] lg:basis-[calc(33.333%-22px)] shrink-0">
              <Link
                to={`/blog/${post.slug}`}
                className="card bg-base-100 hover:shadow-lg transition-shadow group w-full"
              >
                {/* Image */}
                {post.image ? (
                  <figure className="aspect-video overflow-hidden bg-base-300">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-contain"
                    />
                  </figure>
                ) : (
                  <figure className="aspect-video bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                    <span className="text-4xl opacity-30">
                      {post.category === 'Operations' && '🚚'}
                      {post.category === 'Marketing' && '📈'}
                      {post.category === 'Retention' && '🔄'}
                    </span>
                  </figure>
                )}

                <div className="card-body p-4">
                  {/* Category & Read Time */}
                  <div className="flex items-center gap-2 text-xs">
                    <span className="badge badge-primary badge-sm">{post.category}</span>
                    <span className="opacity-60">{post.readTime}</span>
                  </div>

                  {/* Title */}
                  <h3 className="card-title text-base leading-tight group-hover:text-primary transition-colors">
                    {post.title}
                  </h3>

                  {/* Excerpt */}
                  <p className="text-xs opacity-70 line-clamp-2">{post.excerpt}</p>

                  {/* Read More */}
                  <div className="flex items-center gap-1 text-xs text-primary mt-2 group-hover:gap-2 transition-all">
                    Read more
                    <FiArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
        </div>
      </div>
    </section>
  );
}
